/**
 * junctions.ts — Crossroads as a first-class thing, not an accident of overlap.
 *
 * Before this, a junction was simply *where two road slabs happened to cross*.
 * That left three defects, all of them visible:
 *
 *   1. two full-length tarmac slabs overlapping coplanar over the crossing, so
 *      the junction z-fought with itself;
 *   2. the dashed centre line running straight through, which no real road does;
 *   3. the two footways both claiming the same corner squares, z-fighting again.
 *
 * So the carriageway is now drawn in **spans between junctions**, and each
 * junction is its own piece with its own markings. This module is the pure
 * geometry for that: renderer-free, so it can be unit-tested.
 */
import { subtractGaps, type Span } from './pavement';
import {
  carriagewayWidth,
  isStreet,
  laneOffset,
  roadsMeet,
  spanBounds,
  type Orient,
  type RoadSegment,
} from './roads';

/**
 * A crossing, as a rectangle of carriageway centred on (x, y).
 *
 * A **rectangle**, not a square. It was a square while every street in the town
 * was the same width, and it stopped being one the day a high street could meet
 * a back lane: the box is as wide as the vertical road and as deep as the
 * horizontal one, so where they differ it is oblong. Drawing it square instead
 * leaves the wider road's tarmac cut off at the junction mouth.
 */
export interface Junction {
  x: number;
  y: number;
  /** Across x — the width of the vertical road running through it. */
  w: number;
  /** Across y — the width of the horizontal road running through it. */
  h: number;
}

/** One approach into a junction: where its stop line goes, and how long. */
export interface StopLine {
  x: number;
  y: number;
  /** True when the line runs along the x axis (i.e. across a vertical road). */
  alongX: boolean;
  length: number;
}

/**
 * Every place a horizontal road crosses a vertical one.
 *
 * Not a cross-product any more. Once roads have spans, two of them at right
 * angles only meet if **each one's span reaches the other**, so a vertical stub
 * that stops two blocks short of the high street no longer mints a junction
 * square out in the middle of somebody's garden. Lanes are excluded for a
 * different reason: a cobbled way meeting a street is a dropped kerb, not a box
 * of tarmac with stop lines around it.
 */
export function findJunctions(
  segments: readonly RoadSegment[],
  roadWidth: number,
): Junction[] {
  const streets = segments.filter(isStreet);
  const out: Junction[] = [];
  for (const h of streets.filter((s) => s.orient === 'h')) {
    for (const v of streets.filter((s) => s.orient === 'v')) {
      if (!roadsMeet(h, v)) continue;
      out.push({
        x: v.pos,
        y: h.pos,
        w: carriagewayWidth(v, roadWidth),
        h: carriagewayWidth(h, roadWidth),
      });
    }
  }
  return out;
}

/**
 * Which approaches into a junction actually exist along one road.
 *
 * A crossroads has traffic arriving from both directions; a **T-junction** is
 * the same square with one arm missing, because the road ends there. Painting
 * the fourth stop line anyway puts road markings on somebody's front garden, so
 * the arms are derived from the road's own span: an approach exists only where
 * there is road beyond the junction box for something to approach along.
 *
 * `dir` follows the convention in `roads.ts` — +1 arrives from the low side of
 * the axis, −1 from the high side.
 */
export function liveApproaches(seg: RoadSegment, along: number, roadWidth: number): (1 | -1)[] {
  const [from, to] = spanBounds(seg);
  const half = roadWidth / 2;
  const out: (1 | -1)[] = [];
  if (from < along - half) out.push(1);
  if (to > along + half) out.push(-1);
  return out;
}

/**
 * The stretches of one road that are *not* inside a junction — used for both the
 * carriageway slabs and the centre line, so tarmac is drawn exactly once
 * everywhere and the markings break at each crossing.
 */
export function carriagewaySpans(
  extent: number,
  crossings: readonly number[],
  roadWidth: number,
  start = 0,
): Span[] {
  const half = roadWidth / 2;
  return subtractGaps(
    extent,
    crossings.map((c) => ({ from: c - half, to: c + half })),
    start,
  );
}

/**
 * Stop lines for the approaches into a junction along one road.
 *
 * A stop line covers only the lane you'd *arrive* in, not the full carriageway —
 * so it sits on the near half for UK left-hand driving, reusing `laneOffset` from
 * roads.ts rather than re-deriving which side that is.
 *
 * @param along  position of the junction along the road's axis
 * @param across the road's own position (y for 'h', x for 'v')
 * @param dirs   which approaches exist — both, unless a road ends here (see
 *               `liveApproaches`, which is how a T-junction gets three stop
 *               lines rather than four)
 */
export function stopLines(
  orient: Orient,
  along: number,
  across: number,
  roadWidth: number,
  setback: number,
  dirs: readonly (1 | -1)[] = [1, -1],
  /**
   * Width of the road being crossed — how far the junction box reaches along
   * this road's axis. Defaults to this road's own width, which is what it was
   * while every street in the town matched.
   *
   * Two different numbers once they don't. The line's **length** is half this
   * road's carriageway, because it covers the lane you arrive in; where it
   * **sits** is set back past the box, whose extent is the other road's width.
   * Collapsing the two paints the stop line inside the junction wherever a back
   * street meets a high street.
   */
  crossWidth: number = roadWidth,
): StopLine[] {
  const half = roadWidth / 2;
  const boxHalf = crossWidth / 2;
  const laneGap = roadWidth / 4;
  const out: StopLine[] = [];

  // dir +1 arrives from the low side, dir -1 from the high side.
  for (const dir of dirs) {
    const offset = laneOffset(orient, dir, laneGap);
    // Sit just outside the junction square on the approaching side.
    const at = along - dir * (boxHalf + setback);
    out.push(
      orient === 'h'
        ? { x: at, y: across + offset, alongX: false, length: half }
        : { x: across + offset, y: at, alongX: true, length: half },
    );
  }
  return out;
}

/**
 * How wide a gap a road should leave in the *footway* of the road it crosses.
 *
 * Only one orientation may own the corner squares, or both draw them and they
 * z-fight. Horizontal roads' footways run through the corners; vertical ones
 * stop short by the full footway width so they butt up against them instead.
 */
export function footwayGapWidth(
  orient: Orient,
  roadWidth: number,
  pavementWidth: number,
): number {
  return orient === 'h' ? roadWidth : roadWidth + pavementWidth * 2;
}
